<template>
  <Modal
    ref="modalGroupUpload"
    :title="$t('GroupsService.Modal.ModalTitle')"
    width="60%"
    :zIndex="999"
    :has-tab="false"
  >
    <template v-slot:[`no-tab`]>
      <div class="modal-upload">
        <div class="modal-upload-info" v-show="!showForm && !loading">
          <p>
            {{ $t('GroupsService.Modal.headerInstruction') }}
          </p>
          <!--
            Templates duais (plano híbrido §4, D-H6): um link por fluxo
            habilitado — legado baixa o modelo da develop (sempre pt);
            "(Novo)" baixa o modelo por locale do branch.
          -->
          <a
            v-if="flags.legacyImportEnabled"
            data-test-template-legacy
            :href="LEGACY_TEMPLATE_URL"
            download
            >{{ $t('GroupsService.Modal.downloadSheets') }}</a
          >
          <a
            v-if="flags.v2ImportEnabled"
            data-test-template-v2
            :href="_v2TemplateUrl"
            download
            >{{ _v2DownloadLabel }}</a
          >
        </div>

        <!--
          `instruction` explicita: sem a prop, o AddFile cai no texto de
          PeopleService, que fala da coluna personID criando/atualizando "a
          pessoa" — errado no modal de grupos
        -->
        <AddFile
          @file="validateFile"
          :label="fileName"
          :instruction="$t('GroupsService.Modal.fileInstruction')"
        />

        <div v-if="showForm">
          <span class="modal-upload-subtitle">
            {{ $t('GroupsService.Modal.modalsSubtitle') }}
          </span>
          <v-divider></v-divider>
        </div>

        <div v-if="loading" class="loading">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-container fluid v-if="showForm">
          <!--
            Radios por opção { action, flow } (plano híbrido §3.3.1): 3
            legadas (develop) gated por legacyImportEnabled + 4 "(Novo)"
            gated por v2ImportEnabled. Delete NÃO existe no legado (§2.1).
          -->
          <v-radio-group
            v-model="selectedOptionId"
            inline
            class="modal-upload-action"
          >
            <v-radio
              v-for="option in _uploadOptions"
              :key="option.id"
              :data-test-upload-option="option.id"
              :label="$t(option.labelKey)"
              :value="option.id"
            ></v-radio>
          </v-radio-group>
        </v-container>

        <v-form
          ref="form"
          @submit.prevent
          v-show="showForm"
          class="modal-upload-form"
          v-model="valid"
        >
          <v-container>
            <div class="single-input">
              <!--
                :key derivado do contador de limpeza (fix B3): quando a aba
                re-selecionada é a MESMA, a prop selectedItem não muda e o
                v-autocomplete esvaziado pelo form.reset() não re-sincroniza
                — o re-mount garante o select refletindo selectedTab
              -->
              <SingleSelection
                :key="`tab-select-${tabSelectKey}`"
                :selectedItem="selectedTab"
                :label="$t('GroupsService.Modal.labels.groupLabel')"
                :placeholder="$t('GroupsService.Modal.labels.groupLabel')"
                :items="tabs"
                @selectOption="handleTabSelection"
              />
            </div>

            <!--
              Selects do fluxo LEGADO (develop UploadGroupModal): costCenter,
              name, parentGroup, managers, members — restaurados sob as
              opções legadas (R1).
            -->
            <div
              v-if="selectedTab && _isLegacyFlow && _isAddUpdateAction"
              :key="`legacy-add-update-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.costCenter')"
                  :placeholder="$t('GroupsService.Modal.labels.costCenter')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.costCenter || ''"
                  @selectOption="handleSelectOption($event, 'costCenter')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.name')"
                  :placeholder="$t('GroupsService.Modal.labels.name')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.name || ''"
                  @selectOption="handleSelectOption($event, 'name')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.parentGroup')"
                  :placeholder="$t('GroupsService.Modal.labels.parentGroup')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.parentGroup || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'parentGroup')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.managers')"
                  :placeholder="$t('GroupsService.Modal.labels.managers')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.managers || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'managers')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.members')"
                  :placeholder="$t('GroupsService.Modal.labels.members')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.members || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'members')"
                />
              </div>
            </div>

            <!-- Selects do fluxo NOVO (branch): groupID + 9 módulo+papel -->
            <div
              v-if="selectedTab && !_isLegacyFlow && _isAddUpdateAction"
              :key="`add-update-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.groupID')"
                  :placeholder="$t('GroupsService.Modal.labels.groupID')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.groupID || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'groupID')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.costCenter')"
                  :placeholder="$t('GroupsService.Modal.labels.costCenter')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.costCenter || ''"
                  @selectOption="handleSelectOption($event, 'costCenter')"
                />
              </div>
              <!--
                Status do grupo (D-e): a coluna 'Status'/'Estado' era exportada e
                nunca consumida — não havia como ativar/inativar grupo por
                planilha, e reimportar o próprio export perdia o status. O
                attribute é 'groupStatus' para não colidir com o `status` de
                verificação da linha. Opcional/clearable: sem bind, o status do
                grupo fica como está.
              -->
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.groupStatus')"
                  :placeholder="$t('GroupsService.Modal.labels.groupStatus')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.groupStatus || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'groupStatus')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.name')"
                  :placeholder="$t('GroupsService.Modal.labels.name')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.name || ''"
                  @selectOption="handleSelectOption($event, 'name')"
                />
              </div>
              <!--
                Duas fontes de hierarquia no v2: o ID do pai ('Hierarquia ID')
                e o centro de custo do pai. O ID vence quando as duas vierem
                preenchidas (prioridade resolvida no sync-workers) — a maior
                parte das linhas do export só traz o ID, porque o pai não tem
                centro de custo cadastrado.
              -->
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.parentGroupID')"
                  :placeholder="$t('GroupsService.Modal.labels.parentGroupID')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.parentGroupID || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'parentGroupID')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.parentGroup')"
                  :placeholder="$t('GroupsService.Modal.labels.parentGroup')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.parentGroup || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'parentGroup')"
                />
              </div>
              <!--
                Colunas globais legadas (CPF LIDERANÇA/CPF MEMBROS) — alias de
                transição (G3): aplicam manager/member em TODOS os 3 módulos.
                Em conflito com uma coluna módulo+papel específica, a
                específica prevalece (resolvido no backend).
              -->
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.managers')"
                  :placeholder="$t('GroupsService.Modal.labels.managers')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.managers || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'managers')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.members')"
                  :placeholder="$t('GroupsService.Modal.labels.members')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.members || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'members')"
                />
              </div>
              <!--
                Colunas módulo+papel (G3/G4) — headers técnicos canônicos,
                idênticos nos 3 idiomas. Células com CSV de e-mails (G2).
              -->
              <div
                v-for="column in GROUP_MODULE_ROLE_COLUMNS"
                :key="column"
                class="input-holder"
              >
                <SingleSelection
                  :label="
                    $t('GroupsService.Modal.labels.moduleRoleColumn', {
                      column,
                    })
                  "
                  :placeholder="
                    $t('GroupsService.Modal.labels.moduleRoleColumn', {
                      column,
                    })
                  "
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute[column] || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, column)"
                />
              </div>
            </div>
            <div
              v-if="selectedTab && action === GROUPS_ACTIONS.InformationUpdate"
              :key="`information-update-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.oid')"
                  :placeholder="$t('GroupsService.Modal.labels.oid')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.groupID || ''"
                  @selectOption="handleSelectOption($event, 'groupID')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.costCenter')"
                  :placeholder="$t('GroupsService.Modal.labels.costCenter')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.costCenter || ''"
                  @selectOption="handleSelectOption($event, 'costCenter')"
                />
              </div>
              <!--
                Status do grupo (D-e) — information-update também atualiza grupo.
                Este branch é COMPARTILHADO pelos dois fluxos (o legado usa os
                mesmos oid/costCenter/name da develop), então o select é gated em
                `!_isLegacyFlow`: groupStatus é attribute do contrato v2 e não
                pode entrar num payload legado nem por escolha manual.
              -->
              <div v-if="!_isLegacyFlow" class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.groupStatus')"
                  :placeholder="$t('GroupsService.Modal.labels.groupStatus')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.groupStatus || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'groupStatus')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.name')"
                  :placeholder="$t('GroupsService.Modal.labels.name')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.name || ''"
                  @selectOption="handleSelectOption($event, 'name')"
                />
              </div>
            </div>
            <!--
              Branch delete (G5): DELETE real de grupo — SÓ no fluxo v2
              (matriz action×flow §2.1). O contrato de colunas do backend
              (ParamsListSpreadsheedDeleteVerificationGroup) exige name E
              costCenter — as colunas legíveis por humanos; groupID é
              opcional porque o grupo também é resolvido por costCenter/nome
              na verificação (verifyDeleteRow nos workers).
            -->
            <div
              v-if="selectedTab && action === GROUPS_ACTIONS.Delete"
              :key="`delete-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.costCenter')"
                  :placeholder="$t('GroupsService.Modal.labels.costCenter')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.costCenter || ''"
                  @selectOption="handleSelectOption($event, 'costCenter')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.name')"
                  :placeholder="$t('GroupsService.Modal.labels.name')"
                  :items="groupColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.name || ''"
                  @selectOption="handleSelectOption($event, 'name')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('GroupsService.Modal.labels.groupID')"
                  :placeholder="$t('GroupsService.Modal.labels.groupID')"
                  :items="groupColumns"
                  :selectedItem="_columnByAttribute.groupID || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'groupID')"
                />
              </div>
            </div>
          </v-container>
        </v-form>
        <v-divider v-show="showForm && selectedTab"></v-divider>
        <div class="dialog-footer" v-show="showForm && selectedTab">
          <v-btn
            @click="handleDialog()"
            class="dialog-footer-btnCancel"
            depressed
            plain
          >
            {{ $t('GroupsService.modalButtons.cancel') }}
          </v-btn>
          <v-btn
            @click="SendTabsInformations"
            class="dialog-footer-btn"
            depressed
          >
            {{ _buttonLabel }}
          </v-btn>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script>
import { GROUPS_ACTIONS } from '@/helpers/upload-actions'
import {
  SYNC_FLOWS,
  DEFAULT_SYNC_FLAGS,
  GROUPS_UPLOAD_OPTIONS,
  filterUploadOptionsByFlags,
  findUploadOption,
  resolveSyncFlags,
} from '@/helpers/upload-flows'
import { GROUP_MODULE_ROLE_COLUMNS } from '@/helpers/group-module-roles'
import {
  autoMatchColumns,
  GROUPS_SHEET_TAB_NAMES,
  GROUPS_COLUMN_CATALOG,
  GROUPS_AUTO_MATCH_ATTRIBUTES,
  GROUPS_LEGACY_COLUMN_CATALOG,
  GROUPS_LEGACY_AUTO_MATCH_ATTRIBUTES,
} from '@/helpers/column-auto-match'
import { uploadTabSelectionMixin } from '@/mixins/upload-tab-selection'
import { calcTemplateUrlByLocale, LEGACY_TEMPLATE_URL } from '@/helpers/locale'
import AddFile from '@/views/SyncPage/Tabs/DataPage/Tabs/PeoplePage/Partials/UploadModal/Partials/AddFile/AddFile.vue'

import {
  verifySpreadSheet,
  sendTabsInformations,
} from '@/services/groups/index'

export default {
  components: {
    AddFile,
  },
  mixins: [uploadTabSelectionMixin],
  data() {
    return {
      GROUPS_ACTIONS,
      GROUP_MODULE_ROLE_COLUMNS,
      LEGACY_TEMPLATE_URL,
      // nomes esperados da aba do módulo (contrato do mixin de seleção)
      sheetTabCandidates: GROUPS_SHEET_TAB_NAMES,
      // Flags por tenant (GET /sync/settings, D-H13). Default conservador
      // enquanto carrega/em erro: legado visível, "(Novo)" oculto.
      flags: { ...DEFAULT_SYNC_FLAGS },
      selectedOptionId: GROUPS_UPLOAD_OPTIONS[0].id,
      action: GROUPS_UPLOAD_OPTIONS[0].action,
      flow: GROUPS_UPLOAD_OPTIONS[0].flow,
      showForm: false,
      fileName: '',
      loading: false,
      columns: {},
      valid: false,
      tabsInformations: {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      },
      selectedTab: '',
      rules: {
        required: v => {
          const requiredLabel = this.$t('GroupsService.Modal.requiredField')

          if (v instanceof Array && v.length == 0) return requiredLabel
          return !!v || requiredLabel
        },
      },
    }
  },
  async mounted() {
    this.flags = await resolveSyncFlags()
    this.ensureAvailableOption()
  },
  watch: {
    selectedTab() {
      this.reset()
      this.applyAutoMatch()
    },
    // Único orquestrador da troca de opção (qualquer mudança de action E/OU
    // flow): limpa binds + aba e re-executa a auto-seleção da aba (regras
    // B2 no mixin), que re-dispara o auto-match válido para a NOVA opção.
    // Sem watches separados de action/flow — evitava-se rodar reset/
    // auto-match 2x quando os dois mudavam juntos.
    selectedOptionId(id) {
      const option = findUploadOption(GROUPS_UPLOAD_OPTIONS, id)
      if (!option) return
      this.action = option.action
      this.flow = option.flow
      this.resetTabSelectionForOptionChange()
    },
  },
  computed: {
    _uploadOptions() {
      return filterUploadOptionsByFlags(GROUPS_UPLOAD_OPTIONS, this.flags)
    },
    _isLegacyFlow() {
      return this.flow === SYNC_FLOWS.Legacy
    },
    _v2TemplateUrl() {
      return calcTemplateUrlByLocale(this.$i18n.locale)
    },
    _v2DownloadLabel() {
      return this.flags.legacyImportEnabled
        ? this.$t('GroupsService.Modal.downloadSheetsNew')
        : this.$t('GroupsService.Modal.downloadSheets')
    },
    tabs() {
      return this.columns?.tabs?.map(el => el.name)
    },
    groupColumns() {
      return this.columns?.tabs?.find(el => el.name === this.selectedTab)
        ?.columns
    },
    // coluna mapeada por attribute — alimenta o selectedItem dos selects
    // (pré-preenchimento do auto-match e escolha manual ficam em sincronia)
    _columnByAttribute() {
      return this.tabsInformations.tab.columns.reduce(
        (map, { column, attribute }) => ({ ...map, [attribute]: column }),
        {}
      )
    },
    _isAddUpdateAction() {
      return (
        this.action === GROUPS_ACTIONS.FullHierarchyAddUpdate ||
        this.action === GROUPS_ACTIONS.PartialAddUpdate
      )
    },
    _buttonLabel() {
      return this.action === GROUPS_ACTIONS.Delete
        ? this.$t('GroupsService.modalButtons.deleteGroups')
        : this.$t('GroupsService.modalButtons.loadGroup')
    },
  },
  methods: {
    initiliazeData() {
      this.selectOption(this._uploadOptions[0] || GROUPS_UPLOAD_OPTIONS[0])
      this.showForm = false
      this.fileName = ''
      this.columns = {}
      this.selectedTab = ''
      this.tabsInformations = {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      }
    },
    selectOption(option) {
      this.selectedOptionId = option.id
      this.action = option.action
      this.flow = option.flow
    },
    // Se as flags escondem a opção corrente (ex.: legado desligado),
    // seleciona a primeira disponível
    ensureAvailableOption() {
      const available = this._uploadOptions
      if (!available.length) return
      if (!findUploadOption(available, this.selectedOptionId)) {
        this.selectOption(available[0])
      }
    },
    reset() {
      this.tabsInformations.tab.columns = []
      // fix B4 — o v-dialog do Modal renderiza lazy: antes da primeira
      // abertura o form não existe (ex.: watcher disparado pelo
      // ensureAvailableOption no mounted em tenant v2-only)
      this.$refs.form?.reset()
    },
    handleDialog() {
      this.initiliazeData()
      this.$refs.modalGroupUpload.handleDialog()
    },
    // Seleção/auto-seleção de aba: uploadTabSelectionMixin
    // (handleTabSelection, autoSelectTab, resetTabSelectionFor*)
    // Auto-identificação de colunas: pré-preenche o bind coluna→atributo
    // quando o header casa com um nome conhecido (match exato pós-
    // normalização). O catálogo é o do FLUXO selecionado (§3.3.3): opção
    // legada usa os headers do modelo antigo. Reusa handleSelectOption
    // (dedupe e shape garantidos) e NUNCA sobrescreve mapeamento existente.
    applyAutoMatch() {
      if (!this.selectedTab) return

      const catalog = this._isLegacyFlow
        ? GROUPS_LEGACY_COLUMN_CATALOG
        : GROUPS_COLUMN_CATALOG
      const allowedByAction = this._isLegacyFlow
        ? GROUPS_LEGACY_AUTO_MATCH_ATTRIBUTES
        : GROUPS_AUTO_MATCH_ATTRIBUTES

      const mappedAttributes = this.tabsInformations.tab.columns.map(
        el => el.attribute
      )

      autoMatchColumns(
        this.groupColumns || [],
        catalog,
        allowedByAction[this.action] || []
      )
        .filter(({ attribute }) => !mappedAttributes.includes(attribute))
        .forEach(({ column, attribute }) =>
          this.handleSelectOption({ value: column }, attribute)
        )
    },
    handleSelectOption({ value }, name) {
      const remainingColumns = this.tabsInformations.tab.columns.filter(
        el => el.attribute !== name
      )

      // Limpeza (select clearable emite value vazio): remove o mapeamento —
      // attribute opcional volta a ficar FORA do payload (opt-out do
      // auto-match sem precisar trocar de aba/ação)
      if (value === null || value === undefined || value === '') {
        this.tabsInformations.tab.columns = remainingColumns
        return
      }

      this.tabsInformations.tab.columns = [
        ...remainingColumns,
        { column: value, attribute: name },
      ]
    },
    async validateFile(file) {
      // O guard vem ANTES do spinner: ligar loading e so depois descobrir que
      // nao ha arquivo deixava o modal preso no spinner para sempre (o bloco
      // de instrucoes fica oculto com `!showForm && !loading`, e nada mais
      // apagava a flag)
      if (!file) return

      this.loading = true
      this.fileName = file.name
      // flow da opção corrente no verify (fix P1 do review do híbrido):
      // tenant v2-only já parte com flow='v2' (ensureAvailableOption) e
      // passa no gate validateFlowEnabled; o service só anexa o campo
      // quando v2 (legacy segue byte-idêntico à develop). Trocar de radio
      // após o verify é seguro: o parse é flow-agnóstico e o gate roda de
      // novo no POST /import.
      await verifySpreadSheet(file, this.flow)
        .then(response => {
          this.loading = false
          this.showForm = true
          this.columns = response.data
          // Auto-seleção da aba pós-upload (limpa estado de upload
          // anterior antes de re-selecionar)
          this.resetTabSelectionForNewFile()
        })
        .catch(error => {
          this.loading = false
          this.fileName = ''
          this.showForm = false
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
    async SendTabsInformations() {
      // O RETORNO de validate() e sincrono (Vuetify 2: VForm.validate()
      // devolve o resultado na hora). O v-model `valid` so e atualizado pelo
      // watcher de errorBag, que e um watcher do Vue e portanto assincrono —
      // no primeiro clique com um select obrigatorio vazio `valid` ainda era
      // `true` e o envio passava, para o backend recusar com
      // "PARAMS_NOT_SENT" depois do upload em vez de a tela marcar o campo.
      if (!this.$refs.form.validate()) {
        return
      }

      this.tabsInformations.action = this.action
      // flow no payload SÓ no fluxo novo (plano §2.1/aceite F4-ii): a opção
      // legada envia o payload byte-idêntico ao da develop — o Joi do
      // sync-backend aplica o default 'legacy' na escrita
      if (this.flow === SYNC_FLOWS.V2) {
        this.tabsInformations.flow = SYNC_FLOWS.V2
      } else {
        delete this.tabsInformations.flow
      }
      this.tabsInformations.fileLocationId = this.columns.fileLocationId

      this.loading = true
      this.showForm = false

      await sendTabsInformations(this.tabsInformations)
        .then(response => {
          this.$emit('alert', {
            type: 'success',
            message: response.data.message,
          })
          this.loading = false
          this.$emit('updateGroupsList')
          this.handleDialog()
        })
        .catch(error => {
          this.loading = false
          this.showForm = true
          // Falha sem resposta HTTP (rede caiu, CORS, timeout) nao tem
          // error.response: acessar .data.message ali lancava TypeError
          // DENTRO do catch — rejeicao nao tratada e NENHUM feedback na tela.
          // Mesmo fallback do validateFile.
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
  },
}
</script>
<style src="./style.scss" lang="scss" scoped></style>
